import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/hi';
import { DEFAULT_UI_LANGUAGE, getLanguage } from './languageRegistry';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enAuth from './locales/en/auth.json';
import enValidation from './locales/en/validation.json';
import enApprovals from './locales/en/approvals.json';
import enSidebar from './locales/en/sidebar.json';
import enTerms from './locales/en/terms.json';
import enWorkboard from './locales/en/workboard.json';
import enFinance from './locales/en/modules/finance.json';
import enProcurement from './locales/en/modules/procurement.json';
import enManufacturing from './locales/en/modules/manufacturing.json';
import enHrms from './locales/en/modules/hrms.json';
import enGrowth from './locales/en/modules/growth.json';
import enEducation from './locales/en/modules/education.json';

import arCommon from './locales/ar/common.json';
import arNav from './locales/ar/nav.json';
import arAuth from './locales/ar/auth.json';
import arValidation from './locales/ar/validation.json';
import arApprovals from './locales/ar/approvals.json';
import arSidebar from './locales/ar/sidebar.json';
import arTerms from './locales/ar/terms.json';
import arWorkboard from './locales/ar/workboard.json';
import arFinance from './locales/ar/modules/finance.json';
import arProcurement from './locales/ar/modules/procurement.json';
import arManufacturing from './locales/ar/modules/manufacturing.json';
import arHrms from './locales/ar/modules/hrms.json';
import arGrowth from './locales/ar/modules/growth.json';
import arEducation from './locales/ar/modules/education.json';

import hiCommon from './locales/hi/common.json';
import hiNav from './locales/hi/nav.json';
import hiAuth from './locales/hi/auth.json';
import hiValidation from './locales/hi/validation.json';
import hiApprovals from './locales/hi/approvals.json';
import hiSidebar from './locales/hi/sidebar.json';
import hiTerms from './locales/hi/terms.json';
import hiWorkboard from './locales/hi/workboard.json';
import hiFinance from './locales/hi/modules/finance.json';
import hiProcurement from './locales/hi/modules/procurement.json';
import hiManufacturing from './locales/hi/modules/manufacturing.json';
import hiHrms from './locales/hi/modules/hrms.json';
import hiGrowth from './locales/hi/modules/growth.json';
import hiEducation from './locales/hi/modules/education.json';

/** Shell + Wave E module namespaces (page chrome; deep fields still backlog). */
export const I18N_NAMESPACES = [
  'common',
  'nav',
  'auth',
  'validation',
  'approvals',
  'sidebar',
  'terms',
  'workboard',
  'finance',
  'procurement',
  'manufacturing',
  'hrms',
  'growth',
  'education',
] as const;

export const MODULE_COVERAGE_GROUPS: Record<string, string[]> = {
  shell: ['common', 'nav', 'auth', 'validation', 'sidebar'],
  workboard: ['workboard', 'approvals'],
  terminology: ['terms'],
  finance: ['finance', 'terms'],
  procurement: ['procurement', 'terms'],
  growth: ['growth', 'terms'],
  manufacturing: ['manufacturing', 'terms'],
  quality: ['terms'],
  hrms: ['hrms', 'terms'],
  education: ['education', 'terms'],
};

const resources = {
  en: {
    common: enCommon,
    nav: enNav,
    auth: enAuth,
    validation: enValidation,
    approvals: enApprovals,
    sidebar: enSidebar,
    terms: enTerms,
    workboard: enWorkboard,
    finance: enFinance,
    procurement: enProcurement,
    manufacturing: enManufacturing,
    hrms: enHrms,
    growth: enGrowth,
    education: enEducation,
  },
  ar: {
    common: arCommon,
    nav: arNav,
    auth: arAuth,
    validation: arValidation,
    approvals: arApprovals,
    sidebar: arSidebar,
    terms: arTerms,
    workboard: arWorkboard,
    finance: arFinance,
    procurement: arProcurement,
    manufacturing: arManufacturing,
    hrms: arHrms,
    growth: arGrowth,
    education: arEducation,
  },
  hi: {
    common: hiCommon,
    nav: hiNav,
    auth: hiAuth,
    validation: hiValidation,
    approvals: hiApprovals,
    sidebar: hiSidebar,
    terms: hiTerms,
    workboard: hiWorkboard,
    finance: hiFinance,
    procurement: hiProcurement,
    manufacturing: hiManufacturing,
    hrms: hiHrms,
    growth: hiGrowth,
    education: hiEducation,
  },
};

let initialized = false;

export function initI18n(language: string = DEFAULT_UI_LANGUAGE) {
  const lang = getLanguage(language).code;
  if (!initialized) {
    void i18n.use(initReactI18next).init({
      resources,
      lng: lang,
      fallbackLng: DEFAULT_UI_LANGUAGE,
      defaultNS: 'common',
      ns: [...I18N_NAMESPACES],
      interpolation: { escapeValue: false },
      returnNull: false,
      parseMissingKeyHandler: (key, defaultValue) =>
        typeof defaultValue === 'string' && defaultValue.length > 0 ? defaultValue : key,
    });
    initialized = true;
  } else if (i18n.language !== lang) {
    void i18n.changeLanguage(lang);
  }
  applyDocumentDirection(lang);
  applyDayjsLocale(lang);
  return i18n;
}

export function applyDocumentDirection(languageCode: string) {
  const def = getLanguage(languageCode);
  if (typeof document === 'undefined') return;
  document.documentElement.lang = def.bcp47;
  document.documentElement.dir = def.dir;
  document.documentElement.dataset.uiLanguage = def.code;
}

export function applyDayjsLocale(languageCode: string) {
  const def = getLanguage(languageCode);
  dayjs.locale(def.dayjsLocale);
}

export async function changeAppLanguage(languageCode: string) {
  const def = getLanguage(languageCode);
  if (!i18n.isInitialized) {
    initI18n(def.code);
    return;
  }
  await i18n.changeLanguage(def.code);
  applyDocumentDirection(def.code);
  applyDayjsLocale(def.code);
}

export default i18n;
